import { LogOut } from 'lucide-react';
import {
  type ReactElement,
  type MouseEvent as ReactMouseEvent,
  cloneElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { AuthUser } from '@/auth';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { useAuth } from '@/hooks/useAuth';
import { adminRoleLabel } from '@/lib/platformAdmin';
import { cn } from '@/lib/utils';

export function deriveInitials(user: AuthUser | undefined): string {
  if (!user) return '?';
  const structured = [user.given_name, user.family_name].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0,
  );
  if (structured.length > 0) {
    return structured.map((part) => part.trim()[0]!.toUpperCase()).join('');
  }
  const name = typeof user.name === 'string' ? user.name.trim() : '';
  const email = typeof user.email === 'string' ? user.email.trim() : '';
  const source = name || email;
  if (!source) return '?';
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');
}

export function SessionAvatar({ picture, initials }: { picture?: string; initials: string }) {
  const [failed, setFailed] = useState(false);
  const showPicture = Boolean(picture) && !failed;

  return (
    <span
      data-testid="sidebar-session-avatar"
      aria-hidden="true"
      className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary text-xs font-semibold text-muted-foreground"
    >
      {showPicture ? (
        <img
          src={picture}
          alt=""
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        initials
      )}
    </span>
  );
}

type TriggerProps = {
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void;
  'aria-haspopup'?: 'menu';
  'aria-expanded'?: boolean;
};

interface AdminAccountMenuProps {
  trigger: ReactElement<TriggerProps>;
  /** Sidebar opens to the right of the rail; mobile drawer opens upward. */
  placement?: 'sidebar' | 'mobile';
}

export function AdminAccountMenu({ trigger, placement = 'sidebar' }: AdminAccountMenuProps) {
  const { user, logout } = useAuth();
  const access = useAdminAccess();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const name = (typeof user?.name === 'string' && user.name.trim()) || user?.email || 'Signed in';
  const email = typeof user?.email === 'string' ? user.email : undefined;

  useEffect(() => {
    function onDown(event: globalThis.MouseEvent) {
      if (ref.current && event.target instanceof Node && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  if (!user) return null;

  const triggerEl = cloneElement(trigger, {
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    onClick: (event: ReactMouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(event);
      setOpen((value) => !value);
    },
  });

  return (
    <div className="relative" ref={ref}>
      {triggerEl}
      {open ? (
        <div
          role="menu"
          className={cn(
            'z-[10001] w-64 overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-lg',
            placement === 'mobile'
              ? 'absolute bottom-full left-0 mb-2'
              : 'absolute bottom-0 left-full ml-2',
          )}
        >
          <div className="px-2 py-2">
            <p className="truncate text-sm font-medium text-foreground">{name}</p>
            {email ? <p className="truncate text-[11px] text-muted-foreground">{email}</p> : null}
            <p className="mt-0.5 text-[11px] text-muted-foreground">{adminRoleLabel(access)}</p>
          </div>
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void logout({ logoutParams: { returnTo: window.location.origin } });
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm',
              'text-destructive transition-colors hover:bg-destructive/10',
            )}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
